import { useCallback, useEffect, useState } from 'react';
import { Button, Group, Textarea } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconClock, IconPencil } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { RobotRequestFormConfigs, UpdateRobotParams, updateRobot } from '@mygpt/app';
import { createPromptVersion, listPromptVersions } from '@mygpt/app/apis/services/prompt-versions';
import { useBot } from '@mygpt/app/bot';
import { useGPTStore } from '@/store';
import { showNotification } from '@mantine/notifications';
import { OptimizationModal } from './PromptOptimization/OptimizationModal';
import { VersionHistoryDrawer } from './PromptOptimization/VersionHistoryDrawer';

interface Props {
  setRobotInfo: (robotInfo: UpdateRobotParams) => void;
  robotInfo: UpdateRobotParams;
  documentForm: UseFormReturnType<RobotRequestFormConfigs>;
  onOverrideChangeForConfigForm: (prop: string) => (data?: any) => void;
  onApplyComplete: () => void;
}

export const Prompt = (props: Props) => {
  const { t } = useTranslation();
  const { setRobotInfo, robotInfo, onApplyComplete } = props;
  const { setRobotInfo: setRobotInfoStore } = useGPTStore();
  const { botId } = useBot();

  const [mainPrompt, setMainPrompt] = useState(robotInfo.prompt || '');
  const [savedMainPrompt, setSavedMainPrompt] = useState(robotInfo.prompt || '');
  const [saving, setSaving] = useState(false);
  const [mainVersionCount, setMainVersionCount] = useState(0);
  const [optimizationModalOpened, setOptimizationModalOpened] = useState(false);
  const [historyDrawerOpened, setHistoryDrawerOpened] = useState(false);

  const fetchVersionCount = useCallback(async () => {
    if (!botId) return;
    try {
      const res = await listPromptVersions(botId, 'main', 1, 1);
      setMainVersionCount(res.total);
    } catch {
      // ignore
    }
  }, [botId]);

  useEffect(() => {
    fetchVersionCount();
  }, [fetchVersionCount]);

  useEffect(() => {
    setMainPrompt(robotInfo.prompt || '');
    setSavedMainPrompt(robotInfo.prompt || '');
  }, [robotInfo.prompt]);

  const hasChanges = mainPrompt !== savedMainPrompt;

  const handleSave = async () => {
    if (saving) return;
    if (mainPrompt.trim() === '') {
      showNotification({ message: t('errors.prompt_cannot_be_empty'), color: 'red' });
      return;
    }
    setSaving(true);
    try {
      const { aiId, ai_type, discrible, name, robot_type, subject_name } = robotInfo;
      const res = await updateRobot({
        aiId,
        name: name.trim(),
        subject_name: subject_name?.trim() ?? '',
        discrible: discrible.trim(),
        ai_type,
        robot_type,
        prompt: mainPrompt,
      });
      setRobotInfoStore(res.data);
      setRobotInfo({ ...robotInfo, prompt: mainPrompt });
      setSavedMainPrompt(mainPrompt);
      try {
        await createPromptVersion(botId, { prompt: mainPrompt, prompt_type: 'main', source: 'manual' });
      } catch {
        // ignore
      }
      showNotification({ message: t('save_success'), color: 'green.5' });
      onApplyComplete();
      fetchVersionCount();
    } catch (error: any) {
      console.error(`save error: ${error}`);
      showNotification({ message: error.message || 'Save failed', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => setMainPrompt(savedMainPrompt);

  const handleOptimizationApply = async (newPrompt: string, changeSummary?: string) => {
    const { aiId, ai_type, discrible, name, robot_type, subject_name } = robotInfo;
    const res = await updateRobot({
      aiId,
      name: name.trim(),
      subject_name: subject_name?.trim() ?? '',
      discrible: discrible.trim(),
      ai_type,
      robot_type,
      prompt: newPrompt,
    });
    setRobotInfoStore(res.data);
    setRobotInfo({ ...robotInfo, prompt: newPrompt });
    setMainPrompt(newPrompt);
    setSavedMainPrompt(newPrompt);
    try {
      await createPromptVersion(botId, {
        prompt: newPrompt,
        prompt_type: 'main',
        change_summary: changeSummary,
        source: 'ai_optimization',
      });
    } catch {
      // ignore
    }
    showNotification({ message: t('save_success'), color: 'green.5' });
    onApplyComplete();
    fetchVersionCount();
  };

  return (
    <div
      className="relative rounded-xl bg-white flex-1 p-6"
      style={{ boxShadow: '0px 10px 60px 0px rgba(226, 236, 249, 0.50' }}
    >
      <div className="flex justify-end mb-4">
        <Button leftSection={<IconPencil size={16} />} onClick={() => setOptimizationModalOpened(true)}>
          {t('settings.prompt.optimize')}
        </Button>
      </div>

      <div className="mb-2">
        <Group gap="sm" align="center">
          <span className="font-semibold">{t('settings.prompt.agent_prompt_title')}</span>
          <Button
            variant="subtle"
            size="compact-xs"
            leftSection={<IconClock size={14} />}
            onClick={() => setHistoryDrawerOpened(true)}
          >
            {t('settings.prompt.version_history')} ({mainVersionCount})
          </Button>
        </Group>
      </div>
      <Textarea
        placeholder={t('settings.prompt.agent_prompt_title')}
        value={mainPrompt}
        onChange={(e) => setMainPrompt(e.currentTarget.value)}
        minRows={10}
        maxRows={20}
        autosize
        resize="vertical"
      />

      <Group justify="flex-end" mt="xl">
        <Button variant="default" onClick={handleCancel} disabled={!hasChanges}>
          {t('settings.prompt.cancel')}
        </Button>
        <Button onClick={handleSave} loading={saving} disabled={!hasChanges}>
          {t('settings.prompt.save')}
        </Button>
      </Group>

      <OptimizationModal
        opened={optimizationModalOpened}
        onClose={() => setOptimizationModalOpened(false)}
        currentPrompt={mainPrompt}
        promptType="main"
        onApply={handleOptimizationApply}
      />

      <VersionHistoryDrawer
        opened={historyDrawerOpened}
        onClose={() => setHistoryDrawerOpened(false)}
        aiId={botId}
        promptType="main"
        currentPrompt={mainPrompt}
        onVersionActivated={() => {
          onApplyComplete();
          fetchVersionCount();
        }}
      />
    </div>
  );
};
